import type { SupabaseClient } from "@supabase/supabase-js";

import {
  liveCheckinCutoffIso,
  liveCheckinMaxAgeHours,
  type LiveCheckinMaxAgeHours,
} from "./live-checkin-max-age";

import {
  hasOnCallHalo,
  liveDotPaint,
  liveSiteGroupKey,
  type LiveDotPaint,
  type LiveDotPaintInput,
} from "./duty-live-presentation";
import type { DutyPing } from "./duty-pings";
import { freeDutyMarkerId, type FreeDutyPresenceRow } from "./free-duty-live";
import {
  DEFAULT_GPS_ACCURACY_BAND_THRESHOLDS,
  type GpsAccuracyBandThresholds,
} from "./gps-accuracy-bands";
import { gpsAccuracyLine } from "./gps-accuracy-presentation";
import { classifyFieldPin, type FieldPin } from "./field-pin-layers";
import { checkpointDisplayLabel } from "./scan-list";
import { circlePolygonRing } from "../lib/geofence-circle";
import { MAP_THEME } from "../lib/map-theme";

// Map data for the Command Center.
//   Tier-1: public.supervisor_checkpoint_map_points — fixed checkpoint posts (safe: expected
//           coordinates of installed posts, not personal location). Plotted as pins.
//   Tier-2: public.supervisor_scan_points — individual check-in coordinates (privacy-sensitive).
//           Loaded + shown ONLY when the operator opts in (default off); never drawn as a trail.
// Pure mappers + GeoJSON builders so the rendering component sits on a unit-tested core.

export interface CheckpointPoint {
  checkpointId: string;
  name: string;
  lat: number;
  lng: number;
  verificationStatus: string;
  preferredMethod: string | null;
  geofenceRadiusMeters: number | null;
  routeName: string | null;
  siteName: string | null;
  isRequired: boolean;
  /**
   * PRD-001Y Wave C. The map-points view outer-joins route_checkpoints (20260802220000), so a
   * row can now describe a pin with no route. routeId null IS the routed/unrouted signal;
   * routeName is display text and can be null for other reasons, so never classify on it.
   */
  routeId: string | null;
  /** 'provisional' | 'confirmed'. Remotes predating the placement contract read as confirmed. */
  placementState: string;
  placementSource: string;
}

/**
 * Where the plotted coordinates came from.
 * - `scan_gps`: device fix on the scan row (preferred).
 * - `checkpoint_expected`: pin fallback when the check-in had no usable GPS.
 * - `unknown`: remote pre-dates map_coord_source; treat as plotable but unlabeled.
 */
export type ScanMapCoordSource = "scan_gps" | "checkpoint_expected" | "unknown";

export interface ScanPoint {
  scanPointId: string;
  lat: number;
  lng: number;
  checkpointName: string | null;
  scanMethod: string | null;
  assurance: string | null;
  guardName: string | null;
  /** ISO server_received_at when the view serves it; used for Activity matching. */
  serverReceivedAt: string | null;
  routeAssignmentId?: string | null;
  siteId?: string | null;
  mapCoordSource: ScanMapCoordSource;
  /**
   * Device-reported horizontal accuracy in metres when the scan row carried a fix.
   * Null when the plot used a checkpoint fallback or the device omitted accuracy.
   */
  gpsAccuracyMeters: number | null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

/** Finite number or numeric string (PostgREST may serialize some numerics as strings). */
function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function asMapCoordSource(value: unknown): ScanMapCoordSource {
  if (value === "scan_gps" || value === "checkpoint_expected") {
    return value;
  }
  return "unknown";
}

/** Map a Tier-1 row → CheckpointPoint, or null when it has no valid coordinates (can't be plotted). */
export function mapCheckpointPoint(raw: Record<string, unknown>): CheckpointPoint | null {
  const lat = asNumber(raw.expected_lat);
  const lng = asNumber(raw.expected_lng);
  if (lat === null || lng === null) {
    return null;
  }
  return {
    checkpointId: asString(raw.checkpoint_id) ?? "",
    name: asString(raw.checkpoint_name) ?? "Checkpoint",
    lat,
    lng,
    verificationStatus: asString(raw.verification_status) ?? "active",
    preferredMethod: asString(raw.preferred_verification_method),
    geofenceRadiusMeters: asNumber(raw.geofence_radius_meters),
    routeName: asString(raw.route_name),
    siteName: asString(raw.site_name),
    isRequired: raw.is_required === true,
    routeId: asString(raw.route_id),
    placementState: asString(raw.placement_state) ?? "confirmed",
    placementSource: asString(raw.placement_source) ?? "console",
  };
}

/** Field-pin taxonomy for a CAD map point. routeId is the routed signal, not routeName. */
export function checkpointPointFieldPin(point: CheckpointPoint): FieldPin {
  return {
    checkpointId: point.checkpointId,
    name: point.name,
    lat: point.lat,
    lng: point.lng,
    layer: classifyFieldPin({
      placementState: point.placementState,
      isRouted: point.routeId !== null,
    }),
    siteName: point.siteName,
    routeName: point.routeName,
    // Status stays on the pin so a maintenance or archived checkpoint keeps its amber/grey fill
    // instead of being flattened into the settled green by the placement bucket alone.
    verificationStatus: point.verificationStatus,
  };
}

/** Map a Tier-2 row → ScanPoint, or null when it has no valid coordinates. */
export function mapScanPoint(raw: Record<string, unknown>): ScanPoint | null {
  const lat = asNumber(raw.scan_lat);
  const lng = asNumber(raw.scan_lng);
  if (lat === null || lng === null) {
    return null;
  }
  return {
    scanPointId: asString(raw.scan_point_id) ?? "",
    lat,
    lng,
    checkpointName: asString(raw.checkpoint_name),
    scanMethod: asString(raw.scan_method),
    assurance: asString(raw.verification_assurance),
    guardName: asString(raw.guard_name),
    serverReceivedAt: asString(raw.server_received_at),
    routeAssignmentId: asString(raw.route_assignment_id),
    siteId: asString(raw.site_id),
    mapCoordSource: asMapCoordSource(raw.map_coord_source),
    gpsAccuracyMeters: asNumber(raw.gps_accuracy_meters),
  };
}

const CHECKPOINT_STATUS_COLORS: Record<string, string> = MAP_THEME.checkpointStatus;

export function checkpointColor(verificationStatus: string): string {
  return CHECKPOINT_STATUS_COLORS[verificationStatus] ?? MAP_THEME.checkpointStatus.default;
}

export interface PointFeature {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: Record<string, string>;
}

export interface PointFeatureCollection {
  type: "FeatureCollection";
  features: PointFeature[];
}

export interface PolygonFeature {
  type: "Feature";
  geometry: { type: "Polygon"; coordinates: [number, number][][] };
  properties: Record<string, string>;
}

export interface PolygonFeatureCollection {
  type: "FeatureCollection";
  features: PolygonFeature[];
}

/** GeoJSON FeatureCollection of checkpoint pins (coordinates are [lng, lat] per GeoJSON). */
export function toCheckpointFeatureCollection(
  points: readonly CheckpointPoint[],
): PointFeatureCollection {
  return {
    type: "FeatureCollection",
    features: points.map((point) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [point.lng, point.lat] },
      properties: {
        id: point.checkpointId,
        name: point.name,
        status: point.verificationStatus,
        color: checkpointColor(point.verificationStatus),
        site: point.siteName ?? "",
        route: point.routeName ?? "",
      },
    })),
  };
}

/** True when a checkpoint has a plottable GPS geofence ring. */
export function hasGeofence(point: CheckpointPoint): boolean {
  return point.geofenceRadiusMeters !== null && point.geofenceRadiusMeters > 0;
}

/** GeoJSON polygons for configured GPS geofence radii. */
export function toGeofenceFeatureCollection(
  points: readonly CheckpointPoint[],
): PolygonFeatureCollection {
  return {
    type: "FeatureCollection",
    features: points
      .filter(hasGeofence)
      .map((point) => ({
        type: "Feature" as const,
        geometry: {
          type: "Polygon" as const,
          coordinates: [circlePolygonRing(point.lng, point.lat, point.geofenceRadiusMeters!)],
        },
        properties: {
          id: point.checkpointId,
          name: point.name,
        },
      })),
  };
}

type LineFeatureCollection = GeoJSON.FeatureCollection<GeoJSON.LineString>;

/** Breadcrumb polyline for one on-duty assignment (chronological pings). */
export function toDutyTrailFeatureCollection(
  pings: readonly DutyPing[],
  routeAssignmentId: string,
  label: string = "Guard trail",
): LineFeatureCollection {
  const coordinates = pings
    .filter((ping) => ping.lat !== null && ping.lng !== null)
    .map((ping) => [ping.lng!, ping.lat!] as [number, number]);
  if (coordinates.length < 2) {
    return { type: "FeatureCollection", features: [] };
  }
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: { type: "LineString", coordinates },
        properties: { id: routeAssignmentId, label },
      },
    ],
  };
}

export interface DutyPingGuardMeta {
  guardLabel: string;
  siteName: string | null;
  routeName: string | null;
  freshnessState?: "fresh" | "stale";
  freshnessLabel?: string;
  lastPingLabel?: string;
}

/**
 * Live paint inputs per route assignment. Ages and the keep-visible flags come from the desk;
 * the fade and hide maths stay in `duty-live-presentation`. An assignment with no entry keeps
 * the pre-slice-3 behaviour: it always paints, fully opaque.
 */
export type DutyPingLivePaintByAssignment = Readonly<
  Record<string, Omit<LiveDotPaintInput, "dutyCallId">>
>;

/** Latest duty pings with coordinates (live guard positions). */
export function toDutyPingFeatureCollection(
  pings: readonly DutyPing[],
  guardByAssignment: Readonly<Record<string, DutyPingGuardMeta>> = {},
  outsideByAssignment: Readonly<Record<string, boolean>> = {},
  livePaintByAssignment: DutyPingLivePaintByAssignment = {},
  // Account band ceilings from duty policy. Omitted only by callers with no policy in hand,
  // who then grade like the host grades an account with no policy row (25 / 100).
  gpsAccuracyThresholds: GpsAccuracyBandThresholds = DEFAULT_GPS_ACCURACY_BAND_THRESHOLDS,
): PointFeatureCollection {
  const features: PointFeature[] = [];
  for (const ping of pings) {
    if (ping.lat === null || ping.lng === null) {
      continue;
    }
    const assignmentId = ping.routeAssignmentId ?? "";
    const meta = guardByAssignment[assignmentId];
    // Own-property lookup only. An assignment id that happens to spell a prototype member must
    // not hand back a function where a paint input is expected.
    const paintInput = Object.prototype.hasOwnProperty.call(livePaintByAssignment, assignmentId)
      ? livePaintByAssignment[assignmentId]
      : undefined;
    // No paint input means the caller has not opted into the Live age rules (unit callers and
    // any desk that only wants latest positions). Those keep painting at full strength.
    //
    // An opted-in assignment with a null age is not demonstrably current and fails closed through
    // liveDotPaint. Retained coordinates without a readable timestamp belong on history desks,
    // not Command Live.
    let paint: LiveDotPaint | null = null;
    if (paintInput !== undefined) {
      paint = liveDotPaint({ ...paintInput, dutyCallId: ping.dutyCallId });
    }
    if (paint !== null && !paint.paint) {
      continue;
    }
    features.push({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [ping.lng, ping.lat],
      },
      properties: {
        id: assignmentId,
        source: ping.pingSource ?? "",
        guardLabel: meta?.guardLabel ?? "",
        site: meta?.siteName ?? "",
        route: meta?.routeName ?? "",
        siteGroup: liveSiteGroupKey(meta?.siteName),
        freshness: meta?.freshnessState ?? "stale",
        status: meta?.freshnessLabel ?? "STALE",
        lastPing: meta?.lastPingLabel ?? "",
        outsideSite: outsideByAssignment[assignmentId] === true ? "true" : "false",
        onCall: hasOnCallHalo(ping.dutyCallId) ? "true" : "false",
        // Ready-to-show accuracy line, built here so the renderer stays a dumb printer of safe
        // strings. Always present on a duty person mark, because "we do not know how good this
        // fix is" is itself the answer an operator needs; silence would read as precision.
        gpsAccuracy: gpsAccuracyLine(ping.gpsAccuracyMeters, gpsAccuracyThresholds),
        opacity: (paint === null ? 1 : paint.opacity).toFixed(3),
      },
    });
  }
  return { type: "FeatureCollection", features };
}

/** One free-duty officer's Live paint inputs, keyed by the redacted member ref. */
export type FreeDutyLivePaintByMember = Readonly<
  Record<string, Omit<LiveDotPaintInput, "dutyCallId">>
>;

export interface FreeDutyFeatureMeta {
  freshnessState: "fresh" | "stale";
  freshnessLabel: string;
  lastPingLabel: string;
}

/**
 * Free-duty person marks (2026-08-20 lock amend). Deliberately the SAME feature shape the
 * assigned builder above emits, through the SAME paint helpers, because lock Q2 B says free-duty
 * uses the same LIVE / STALE / NO PING language and the same 4x hide as everyone else. Forking
 * the age math here is how the two families would quietly drift apart.
 *
 * Two properties differ and only two: `id` is namespaced so a free-duty mark can never collide
 * with a route assignment id in the cluster planner, and `freeDuty` carries the cue that stops
 * dispatch reading these officers as standing a post. `route` is deliberately empty rather than
 * a placeholder, because inventing a route name is exactly the fake post the lock forbids.
 */
export function toFreeDutyFeatureCollection(
  rows: readonly FreeDutyPresenceRow[],
  metaByMember: Readonly<Record<string, FreeDutyFeatureMeta>> = {},
  livePaintByMember: FreeDutyLivePaintByMember = {},
  gpsAccuracyThresholds: GpsAccuracyBandThresholds = DEFAULT_GPS_ACCURACY_BAND_THRESHOLDS,
): PointFeatureCollection {
  const features: PointFeature[] = [];
  for (const row of rows) {
    if (row.lat === null || row.lng === null) {
      continue;
    }
    // Own-property lookups only, so a member ref spelling a prototype member cannot hand back a
    // function where a paint input or a meta record is expected.
    const paintInput = Object.prototype.hasOwnProperty.call(livePaintByMember, row.memberRef)
      ? livePaintByMember[row.memberRef]
      : undefined;
    const meta = Object.prototype.hasOwnProperty.call(metaByMember, row.memberRef)
      ? metaByMember[row.memberRef]
      : undefined;
    // Same rule as the assigned builder: no paint input means the caller has not opted into the
    // Live age rules, while an opted-in null age fails closed because it is not current evidence.
    let paint: LiveDotPaint | null = null;
    if (paintInput !== undefined) {
      paint = liveDotPaint({ ...paintInput, dutyCallId: row.dutyCallId });
    }
    if (paint !== null && !paint.paint) {
      continue;
    }
    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: [row.lng, row.lat] },
      properties: {
        id: freeDutyMarkerId(row.memberRef),
        source: row.pingSource ?? "",
        guardLabel: row.guardLabel,
        site: row.siteName ?? "",
        route: "",
        siteGroup: liveSiteGroupKey(row.siteName),
        freshness: meta?.freshnessState ?? "stale",
        status: meta?.freshnessLabel ?? "STALE",
        lastPing: meta?.lastPingLabel ?? "",
        // Free-duty has no assignment, so there is no assigned property to be outside of. The
        // geofence engine also skips null-assignment pings, so claiming either answer would be
        // an invention.
        outsideSite: "false",
        onCall: hasOnCallHalo(row.dutyCallId) ? "true" : "false",
        // Same accuracy line, same helper, same wording as the assigned family. An officer with
        // no post is not an officer with no fix quality.
        gpsAccuracy: gpsAccuracyLine(row.gpsAccuracyMeters, gpsAccuracyThresholds),
        freeDuty: "true",
        note: FREE_DUTY_NOTE,
        opacity: (paint === null ? 1 : paint.opacity).toFixed(3),
      },
    });
  }
  return { type: "FeatureCollection", features };
}

/** The one honest sentence for a marker with no post. Mirrors the phone's line (lock Q7 B). */
export const FREE_DUTY_NOTE = "On duty, no route assigned";

function mapCoordSourceLabel(source: ScanMapCoordSource): string {
  if (source === "scan_gps") return "Scan GPS";
  if (source === "checkpoint_expected") return "Checkpoint pin (fallback)";
  return "Map point";
}

/** GeoJSON FeatureCollection of Tier-2 check-in dots (no trail; discrete points only). */
export function toScanFeatureCollection(
  points: readonly ScanPoint[],
  options?: {
    whenLabel?: (iso: string | null) => string;
    gpsAccuracyThresholds?: GpsAccuracyBandThresholds;
  },
): PointFeatureCollection {
  const whenLabel = options?.whenLabel;
  const gpsAccuracyThresholds = options?.gpsAccuracyThresholds ?? DEFAULT_GPS_ACCURACY_BAND_THRESHOLDS;
  return {
    type: "FeatureCollection",
    features: points.map((point) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [point.lng, point.lat] },
      properties: {
        id: point.scanPointId,
        // Shared title rule with Activity/Reports. The view serves no presence_kind column here,
        // so free-form dots are recognised by scan_method MANUAL; either way the popup names the
        // dot instead of falling through to the generic "Map feature" heading.
        checkpoint: checkpointDisplayLabel(point),
        name: checkpointDisplayLabel(point),
        method: point.scanMethod ?? "",
        assurance: point.assurance ?? "",
        guard: point.guardName ?? "",
        status: mapCoordSourceLabel(point.mapCoordSource),
        layerLabel: "Check-in",
        when: whenLabel !== undefined ? whenLabel(point.serverReceivedAt) : "",
        // Same vocabulary as Live duty cards and Timeclock. "Scan GPS" above is the
        // coordinate source, not strength; this line is the strength claim.
        gpsAccuracy: gpsAccuracyLine(point.gpsAccuracyMeters, gpsAccuracyThresholds),
      },
    })),
  };
}

const MAP_POINT_LIMIT = 2000;
export const CHECKPOINT_POINT_COLUMNS =
  "checkpoint_id, checkpoint_name, expected_lat, expected_lng, verification_status, preferred_verification_method, geofence_radius_meters, route_id, route_name, site_name, is_required, placement_state, placement_source";
export const SCAN_POINT_COLUMNS =
  "scan_point_id, scan_lat, scan_lng, map_coord_source, gps_accuracy_meters, server_received_at, route_assignment_id, site_id, checkpoint_name, scan_method, verification_assurance, guard_name";

/**
 * Hosted remotes that have not yet applied 20260809120000 still serve the pre-COALESCE
 * scan_points contract (GPS-only rows, no map_coord_source). Retry keeps CAD lit.
 * gps_accuracy_meters has been on the view since the first scan-points migration.
 */
const SCAN_POINT_LEGACY_COLUMNS =
  "scan_point_id, scan_lat, scan_lng, gps_accuracy_meters, checkpoint_name, scan_method, verification_assurance, guard_name";

/**
 * WAVE_C_DEPLOY_FALLBACK column list: everything the view served before 20260802220000.
 * The console auto-deploys from main while migrations are applied by hand afterwards, so for a
 * few minutes a build that asks for placement_state can hit a view that does not have it yet.
 * PostgREST answers a missing column with 42703 and the whole CAD map would go dark on a read
 * that is only asking for extra styling detail. Retrying without the two new columns keeps the
 * map alive: the pre-migration view is still INNER JOINed, so every row it returns really is a
 * routed checkpoint, and the taxonomy degrades to exactly that rather than to a guess.
 */
const CHECKPOINT_POINT_LEGACY_COLUMNS =
  "checkpoint_id, checkpoint_name, expected_lat, expected_lng, verification_status, preferred_verification_method, geofence_radius_meters, route_id, route_name, site_name, is_required";

const UNDEFINED_COLUMN = "42703";

/** Tier-1 checkpoint pins (rows without coordinates are dropped — they can't be mapped). */
export async function fetchCheckpointPoints(
  client: SupabaseClient,
  accountId: string,
): Promise<CheckpointPoint[]> {
  const read = async (columns: string) =>
    client
      .from("supervisor_checkpoint_map_points")
      .select(columns)
      .eq("account_id", accountId)
      .limit(MAP_POINT_LIMIT);

  let { data, error } = await read(CHECKPOINT_POINT_COLUMNS);
  if (error !== null && error.code === UNDEFINED_COLUMN) {
    ({ data, error } = await read(CHECKPOINT_POINT_LEGACY_COLUMNS));
  }
  if (error !== null) {
    throw new Error("Could not load checkpoint map points.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return (data as unknown as Array<Record<string, unknown>>)
    .map((row) => mapCheckpointPoint(row))
    .filter((point): point is CheckpointPoint => point !== null);
}

export interface FetchScanPointsOptions {
  maxAgeHours?: LiveCheckinMaxAgeHours;
  nowMs?: number;
}

function scanWithinLiveWindow(point: ScanPoint, cutoffMs: number): boolean {
  if (point.serverReceivedAt === null || point.serverReceivedAt.length === 0) {
    return false;
  }
  const receivedMs = Date.parse(point.serverReceivedAt);
  if (!Number.isFinite(receivedMs)) {
    return false;
  }
  return receivedMs >= cutoffMs;
}

/** Tier-2 check-in dots — privacy-sensitive; fetched only when the operator opts in. */
export async function fetchScanPoints(
  client: SupabaseClient,
  accountId: string,
  options?: FetchScanPointsOptions,
): Promise<ScanPoint[]> {
  const maxAgeHours = liveCheckinMaxAgeHours(options?.maxAgeHours);
  const nowMs = options?.nowMs ?? Date.now();
  const cutoffIso = liveCheckinCutoffIso(maxAgeHours, nowMs);
  const cutoffMs = Date.parse(cutoffIso);

  const read = async (columns: string, withAgeFilter: boolean) => {
    let query = client
      .from("supervisor_scan_points")
      .select(columns)
      .eq("account_id", accountId)
      .order("server_received_at", { ascending: false });
    if (withAgeFilter) {
      query = query.gte("server_received_at", cutoffIso);
    }
    return query.limit(MAP_POINT_LIMIT);
  };

  let data: unknown = null;
  let error: { code?: string; message?: string } | null = null;

  {
    const first = await read(SCAN_POINT_COLUMNS, true);
    data = first.data;
    error = first.error;
  }
  if (error !== null && error.code === UNDEFINED_COLUMN) {
    // Legacy view may lack map_coord_source. Retry the pre-COALESCE column set so CAD stays lit
    // until the host migration is applied.
    const legacy = await client
      .from("supervisor_scan_points")
      .select(SCAN_POINT_LEGACY_COLUMNS)
      .eq("account_id", accountId)
      .limit(MAP_POINT_LIMIT);
    data = legacy.data;
    error = legacy.error;
  }
  if (error !== null) {
    throw new Error("Could not load check-in points.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return (data as Array<Record<string, unknown>>)
    .map((row) => mapScanPoint(row))
    .filter((point): point is ScanPoint => point !== null)
    .filter((point) => scanWithinLiveWindow(point, cutoffMs));
}
